import React from "react"
import Title from "@love-irish/core-title"
import { IconBook, IconBook2, IconSearch } from "@tabler/icons-react"
import classNames from "classnames"
import Tooltip from "design_system/core/components/tooltip"
import Content from "@love-irish/core-content"
import formatter from "design_system/utils/formatter"
import UserPopover from "@love-irish/user-popover"
import "./index.css"

import { ResourceActions } from "../shared/resource_actions"
import CustomFieldBadge from "@love-irish/resource-custom-field-badge"
import ProfileCurrentLevel from "@love-irish/community-profile-current-level"
import AudioPlayer from "./audio_player"
import { actionKeys, actionTranslations, labels } from "@love-irish/i18n"
import { ReadMoreLink, TypeLabel } from "../shared"
import {
  CodexMeta,
  CodexTagList,
} from "@love-irish/resource-codex-meta-components"
import PronunciationLabelGroup from "@love-irish/resource-pronunciation-label"
import { format as toOghamScript } from "@love-irish/adventure-ogham-script"
import { LanguageFlags } from "@love-irish/cms-language-selector"

// We support a simple version of liquid type tags in codex record names to help
// with examples. Right now we use @grug as the data source for the dynamic tag.
export function addDynamicTags(string = "") {
  return string.replace("_name_", "_@grug_")
}

const CodexRecord = ({
  className = "",
  content = null,
  addon = true,
  addToCollection = true,
  footer = false,
  displayAudioInfo = false,
  level = 0,
  display,
  item,
  collections,
  currentUser,
  ...props
}) => {
  // Chrome options (default to the card look). A divided list passes { bordered: false } etc.
  const bordered = display?.bordered ?? true
  const showTypeLabel = display?.showTypeLabel ?? true
  const readMore = display?.readMore ?? true
  // The record name transliterated into the Ogham alphabet (empty when the name has no letters).
  const oghamName = item?.name ? toOghamScript(item.name) : ""

  return (
    <div
      className={classNames(
        "bg-background min-w-[100px] max-w-full",
        bordered && "border rounded-xl dark:border-dark-700",
        className
      )}
      data-testid="codex-record"
    >
      {showTypeLabel && (
        <div className="px-4 pt-4 text-xs flex gap-1 justify-between items-center">
          <TypeLabel
            icon={IconBook}
            label="Codex record"
            tooltip="Dictionary words or phrases that can gain experience in certain parts of the app"
          />
          {item.levelProgress?.currentLevel && (
            <ProfileCurrentLevel progress={item.levelProgress} />
          )}
        </div>
      )}
      <div className={bordered ? "p-4" : "py-4"}>
        <div className="flex justify-between items-start gap-8">
          <Title
            className="post-title text-2xl text-definition flex gap-2"
            title={addDynamicTags(item.name)}
            size="sm"
            showIcon={false}
            href={item.path}
            {...props}
          />
          <div className="flex shrink-0 gap-1">
            {item.audioUrl && <AudioPlayer audioUrl={item.audioUrl} />}

            {addToCollection && <ResourceActions item={item} />}
          </div>
        </div>

        <div className="space-y-4 mt-2">
          <PronunciationLabelGroup resource={item} />

          <CodexTagList item={item} />
        </div>

        <div className="flex gap-8 justify-between mt-3">
          <div>
            {item.sections && item.sections.length > 0 ? (
              // Enriched with senses: render the POS-grouped section format (condensed).
              <div className="mt-2">
                <CodexMeta item={item} showPronunciation={false} />
              </div>
            ) : (
              <>
                {item.translation && (
                  <div className="mt-2">
                    <p className="text-xs text-secondary uppercase mb-2">
                      {labels.translation.translation} / {labels.translation.text}
                    </p>
                    <Content
                      content={addDynamicTags(item.translation)}
                      className="prose-sm text-inherit text-sm text-dark dark:text-white font-mono! first-line:not-italic"
                    />
                  </div>
                )}

                {(item.summary || item.content) && (
                  <div className="mt-2">
                    <Content
                      content={item.summary || item.content}
                      className="prose-sm text-inherit text-sm text-dark dark:text-white font-mono! first-line:not-italic"
                    />
                  </div>
                )}
              </>
            )}

            {/* Author + created date, like the show page byline (above the read-more link). */}
            {(item.profile?.username || item.publishedAt || item.createdAt) && (
              <p className="mt-4 text-xs text-secondary-600">
                Created{" "}
                {item.profile?.slug && (
                  <>
                    by{" "}
                    <strong>
                      {/* Hover card surfaces the author's metadata + cosmetics; the query fires only on
                          hover and Apollo caches by profile id, so repeated authors cost one request. */}
                      <UserPopover
                        username={item.profile.slug}
                        level={item.profile.currentLevel}
                      >
                        {item.profile.username}
                      </UserPopover>
                    </strong>{" "}
                  </>
                )}
                {(item.publishedAt || item.createdAt) && (
                  <>
                    on{" "}
                    <time className="font-bold">
                      {formatter.shortDate(item.publishedAt || item.createdAt)}
                    </time>
                  </>
                )}
                {item.updatedAt && (
                  <span className="text-secondary-500">
                    {" · updated "}
                    <time>{formatter.shortDate(item.updatedAt)}</time>
                  </span>
                )}
              </p>
            )}

            {item.languages && item.languages.length > 0 && (
              <div className="mt-2">
                <LanguageFlags codes={item.languages} size={16} className="mt-4" />
              </div>
            )}

            {oghamName && (
              <div className="mt-3">
                <Tooltip
                  title={`'${item.name}' in Ogham, an ancient Celtic writing system`}
                >
                  {/* aria-label (not role="img") gives screen readers the name without colliding
                      with the item image's role; the glyphs alone would read as gibberish. */}
                  <span
                    data-testid="codex-ogham"
                    aria-label={`'${item.name}' in Ogham, an ancient Celtic writing system`}
                    className="ogham-glyph text-xl leading-none text-secondary-500"
                  >
                    {oghamName}
                  </span>
                </Tooltip>
              </div>
            )}

            {readMore !== false && (
              <ReadMoreLink
                href={item.path}
                {...(typeof readMore === "object" ? readMore : {})}
              />
            )}
          </div>

          {item.imageUrl && (
            <div className="w-24 md:w-[256px] aspect-square pixel-art mt-4 shrink-0">
              <img src={item.imageUrl} className="w-full block" />
            </div>
          )}
        </div>
      </div>

      {item.customFields && item.customFields.length > 0 && (
        <div className="p-2">
          <hr />
          <div className="mt-2 flex flex-wrap gap-2">
            {item.customFields.map((field) => (
              <CustomFieldBadge key={field.key} field={field} />
            ))}
          </div>
        </div>
      )}

      {item.audioUrl && (
        <AudioPlayer audioUrl={item.audioUrl} showDetailedPlayer={true} />
      )}

      {footer && (
        <div className="p-3 text-xs  rounded-b border-t border-secondary-200 flex gap-2 bg-secondary-50 text-secondary-600 dark:bg-dark-900 dark:text-secondary-100 dark:border-dark-500">
          <IconSearch
            className="text-secondary-400 inline-block align-middle"
            size={14}
          />
          <div>
            {level === 1 ? "Unlocked a new codex record!" : "Found again!"}{" "}
            <span className="text-warning font-bold dark:text-warning-400">
              + 5 EXP
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default CodexRecord
